
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowDownToLine, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/services/LoggingService";

const COMPONENT = 'DocumentItem';

interface DocumentItemProps {
  id: string;
  fileName: string;
  filePath: string;
  displayTitle: string;
  canManageDocuments: boolean;
  onDelete: (docId: string) => void;
}

export function DocumentItem({ 
  id, 
  fileName, 
  filePath, 
  displayTitle,
  canManageDocuments,
  onDelete 
}: DocumentItemProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState(displayTitle);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const { data, error } = await supabase.storage
        .from('fund_documents')
        .download(filePath);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error(COMPONENT, 'Download error:', { error, fileName, filePath });
      toast({
        title: "Download failed",
        description: "There was an error downloading the document.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      
      // First try to delete from storage
      const { error: storageError } = await supabase.storage
        .from('fund_documents')
        .remove([filePath]);

      if (storageError) {
        throw storageError;
      }

      // If storage deletion successful, delete from database
      const { error: dbError } = await supabase
        .from('fund_documents')
        .delete()
        .eq('id', id);

      if (dbError) {
        throw dbError;
      }

      toast({
        title: "Document deleted",
        description: "The document has been successfully deleted.",
      });

      onDelete(id);
    } catch (error) {
      logger.error(COMPONENT, 'Delete error:', { error, fileName, filePath });
      toast({
        title: "Deletion failed",
        description: "There was an error deleting the document.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditSave = async () => {
    try {
      const { error } = await supabase
        .from('fund_documents')
        .update({ display_title: editValue })
        .eq('id', id);

      if (error) throw error;
      
      setEditingId(null);
      toast({
        title: "Title updated",
        description: "Document title has been updated successfully.",
      });
    } catch (error) {
      logger.error(COMPONENT, 'Update error:', { error, fileName });
      toast({
        title: "Update failed",
        description: "Failed to update document title.",
        variant: "destructive",
      });
      setEditValue(displayTitle); // Reset to original value
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditValue(displayTitle); // Reset to original value
    }
  };

  return (
    <div className="flex flex-col p-3 bg-muted rounded-lg overflow-hidden">
      <div className="flex items-center justify-between">
        {canManageDocuments && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={isDeleting}
            className="mr-2 h-8 w-8 p-0"
          >
            {isDeleting ? (
              <Spinner size="sm" className="text-muted-foreground" ariaLabel="Deleting" />
            ) : (
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            )}
          </Button>
        )}
        <div className="flex-1 mr-4">
          {editingId === id && canManageDocuments ? (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleEditSave}
              onKeyDown={handleKeyPress}
              autoFocus
              className="w-full"
            />
          ) : (
            <div 
              onClick={() => canManageDocuments && setEditingId(id)}
              className={canManageDocuments ? "cursor-pointer hover:text-primary" : ""}
            >
              <span className="text-base font-medium block">
                {displayTitle}
              </span>
              {canManageDocuments && (
                <span className="text-xs text-muted-foreground block">
                  {fileName}
                </span>
              )}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          disabled={isDownloading}
          className="shrink-0"
        >
          {isDownloading ? (
            <Spinner size="sm" className="mr-2" ariaLabel="Downloading" />
          ) : (
            <ArrowDownToLine className="h-4 w-4 mr-2" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
}
